import React, { useState } from 'react';
import styled from 'styled-components';

import COLORS from 'constants/colors';
import ConfirmationOverlay from 'shared/ConfirmationOverlay';
import Icon from 'components/elements/Icon';

export interface StepConfirmationField {
  label: string;
  value: string;
  suffix?: string;
}

export interface StepConfirmationOverlayProps {
  identifier: string;
  stepLabel: string;
  fields: StepConfirmationField[];
  onConfirmClick: (event: React.MouseEvent) => void;
  onCancelClick: (event: React.MouseEvent) => void;
}

const Lead = styled.p`
  margin-bottom: 1rem;
  font-weight: bold;
`;

// Los valores que se van a congelar, destacados: es lo unico que el analista tiene que
// revisar antes de aceptar, porque despues no los puede corregir.
const Values = styled.ul`
  margin: 0 0 1rem;
  border-left: 4px solid ${COLORS.BLUE};
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.04);
  list-style: none;

  li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.35rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-variant-numeric: tabular-nums;
  }
`;

const Warning = styled.p`
  align-items: flex-start;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-left: 4px solid ${COLORS.RED};
  padding: 0.75rem 1rem;
  background: rgba(177, 42, 49, 0.08);
`;

const Acknowledge = styled.label`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: bold;
`;

// PLA-1077: confirmacion de cada paso del analisis fisico. Es el control que reemplaza a
// poder editar: una vez aceptada, los campos del paso quedan congelados.
function StepConfirmationOverlay({
  identifier,
  stepLabel,
  fields,
  onConfirmClick,
  onCancelClick,
}: StepConfirmationOverlayProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <ConfirmationOverlay
      id={`${identifier}_step_confirmation`}
      title={I18n.translate('physical_analysis.step_confirmation.title')}
      titlePrefix={identifier}
      headerSubtitle={stepLabel}
      confirmText={I18n.translate('physical_analysis.step_confirmation.confirm')}
      cancelText={I18n.translate('buttons.cancel')}
      onConfirmClick={onConfirmClick}
      onCancelClick={onCancelClick}
      primaryDisabled={!acknowledged}
    >
      <Lead>{I18n.translate('physical_analysis.step_confirmation.lead')}</Lead>

      <Values>
        {fields.map((field) => (
          <li key={field.label}>
            <span>{field.label}</span>
            <strong>
              {field.value === '' ? '--' : field.value}
              {field.suffix ? ` ${field.suffix}` : ''}
            </strong>
          </li>
        ))}
      </Values>

      <Warning>
        <Icon icon='cross' color={COLORS.RED} />

        {I18n.translate('physical_analysis.step_confirmation.warning')}
      </Warning>

      <Acknowledge>
        <input
          type='checkbox'
          checked={acknowledged}
          data-cy='step_confirmation_acknowledge'
          onChange={(event) => setAcknowledged(event.target.checked)}
        />

        {I18n.translate('physical_analysis.step_confirmation.acknowledge')}
      </Acknowledge>
    </ConfirmationOverlay>
  );
}

export default StepConfirmationOverlay;
